import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import Layout from "@/components/Layout";
import AcknowledgementCeremony from "@/components/AcknowledgementCeremony";
import GirribalAccessGate from "@/components/GirribalAccessGate";
import ConsentBanner from "@/components/ConsentBanner";
import { trackPageView, trackEvent } from "@/lib/analytics";
import Welcome from "@/pages/Welcome";
import Home from "@/pages/Home";
import Architecture from "@/pages/Architecture";
import Pricing from "@/pages/Pricing";
import Story from "@/pages/Story";
import Nations from "@/pages/Nations";
import Assurance from "@/pages/Assurance";
import Contact from "@/pages/Contact";
import DesignPrinciples from "@/pages/DesignPrinciples";
import Readiness from "@/pages/Readiness";
import CapabilityStatement from "@/pages/CapabilityStatement";
import Research from "@/pages/Research";
import PrivacyPreferences from "@/pages/PrivacyPreferences";
import FunctionalityReport from "@/pages/FunctionalityReport";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProtectedRoute from "@/components/AdminProtectedRoute";
import Privacy from "@/pages/legal/Privacy";
import Terms from "@/pages/legal/Terms";
import ICIPNotice from "@/pages/legal/ICIPNotice";
import BackgroundIP from "@/pages/legal/BackgroundIP";
import NotFound from "@/pages/NotFound";

// Gate pass uses sessionStorage (clears when browser/tab closes) PLUS an
// idle timeout — after 30 minutes of no activity, the gate re-engages.
const GATE_KEY = "bgb_girribal_passed_at";
const ACK_KEY = "bgb_acknowledgement_received";
const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const CHECK_INTERVAL_MS = 30 * 1000;    // re-check expiry every 30s

function isStillValid() {
  const ts = sessionStorage.getItem(GATE_KEY);
  if (!ts) return false;
  return Date.now() - parseInt(ts, 10) < IDLE_TIMEOUT_MS;
}

// Tracks pageviews on every route change (consent-gated inside trackPageView).
function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

function App() {
  const [acknowledged, setAcknowledged] = useState(false);
  const [passed, setPassed] = useState(false);
  const lastActivityRef = useRef(Date.now());

  // Initial check on mount
  useEffect(() => {
    if (sessionStorage.getItem(ACK_KEY)) setAcknowledged(true);
    if (isStillValid()) setPassed(true);
  }, []);

  // Refresh activity timestamp on user interaction (rolling idle window)
  useEffect(() => {
    if (!passed) return;
    const onActivity = () => {
      lastActivityRef.current = Date.now();
      sessionStorage.setItem(GATE_KEY, String(Date.now()));
    };
    const events = ["mousemove", "keydown", "scroll", "touchstart", "click"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, onActivity));
  }, [passed]);

  // Periodic idle check
  useEffect(() => {
    if (!passed) return;
    const id = setInterval(() => {
      if (!isStillValid()) {
        sessionStorage.removeItem(GATE_KEY);
        setPassed(false);
      }
    }, CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [passed]);

  const handleEnter = () => {
    sessionStorage.setItem(GATE_KEY, String(Date.now()));
    setPassed(true);
    trackEvent("girribal_gate_passed");
  };

  const handleAcknowledged = () => {
    sessionStorage.setItem(ACK_KEY, String(Date.now()));
    setAcknowledged(true);
    trackEvent("acknowledgement_received");
  };

  // Admin routes bypass the Acknowledgement + Girribal gates entirely —
  // those are visitor-facing rituals; admin access is operational.
  const isAdminPath = typeof window !== "undefined" && window.location.pathname.startsWith("/admin");

  if (!isAdminPath && !acknowledged) {
    return (
      <div className="App">
        <AcknowledgementCeremony onContinue={handleAcknowledged} />
      </div>
    );
  }

  if (!isAdminPath && !passed) {
    return (
      <div className="App">
        <GirribalAccessGate onEnter={handleEnter} />
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <PageViewTracker />
        <Routes>
          {/* Admin (outside the public Layout chrome) */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin"
            element={
              <AdminProtectedRoute>
                <AdminDashboard />
              </AdminProtectedRoute>
            }
          />

          <Route element={<Layout />}>
            <Route path="/" element={<Welcome />} />
            <Route path="/start" element={<Home />} />
            <Route path="/story" element={<Story />} />
            <Route path="/nations" element={<Nations />} />
            <Route path="/design" element={<DesignPrinciples />} />
            <Route path="/architecture" element={<Architecture />} />
            <Route path="/research" element={<Research />} />
            {/* IP-protected: methodology detail pages redirect to contact */}
            <Route path="/standards" element={<Navigate to="/contact" replace />} />
            <Route path="/toolkit" element={<Navigate to="/contact" replace />} />
            <Route path="/platform" element={<Navigate to="/contact" replace />} />
            <Route path="/demo" element={<Navigate to="/contact" replace />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/readiness" element={<Readiness />} />
            <Route path="/capability-statement" element={<CapabilityStatement />} />
            <Route path="/product-catalogue" element={<Navigate to="/contact" replace />} />
            <Route path="/privacy-preferences" element={<PrivacyPreferences />} />
            <Route path="/internal/functionality-report" element={<FunctionalityReport />} />
            <Route path="/assurance" element={<Assurance />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/legal/privacy" element={<Privacy />} />
            <Route path="/legal/terms" element={<Terms />} />
            <Route path="/legal/icip" element={<ICIPNotice />} />
            <Route path="/legal/background-ip" element={<BackgroundIP />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" />
      <ConsentBanner />
    </div>
  );
}

export default App;
